import { useCallback } from 'react';

import { DeliveryChip } from './DeliveryChip';
import useNavigateToDeliveryDetailPage from 'hooks/useNavigateToDeliveryDetailPage';
import { useLanguage } from 'context/AppContext';
import { useDelivery } from 'api/hooks/useDelivery';

type TurnaroundOriginProps = {
  variant: 'turnaroundOrigin';

  // We can't guarantee that the data contains a turnaroundDestinationDeliveryId and we still want to display the chip so we default to null
  turnaroundDestinationDeliveryId: number | null;
};

type TurnaroundDestinationProps = {
  variant: 'turnaroundDestination';
  turnaroundOriginDeliveryId: number | null;
};

type Props = TurnaroundOriginProps | TurnaroundDestinationProps;

export const TurnaroundDeliveryChip = ({ variant, ...props }: Props) => {
  const [lang] = useLanguage();

  // We want to ensure that turnaroundDestinationDeliveryId is passed for the turnaroundOrigin variant and vice versa
  // So we use a discriminated union and type cast to ensure that the correct property is passed
  const turnaroundDeliveryId =
    variant === 'turnaroundOrigin'
      ? (props as TurnaroundOriginProps).turnaroundDestinationDeliveryId
      : (props as TurnaroundDestinationProps).turnaroundOriginDeliveryId;

  // We first need to fetch the delivery to check the status so that we know if we should go to check in or check out
  // TODO: Remove this once we have a declarative delivery info page
  const { data: delivery, isLoading: isLoadingDelivery } = useDelivery(turnaroundDeliveryId);

  const navigateToDeliveryDetailPage = useNavigateToDeliveryDetailPage();

  const onClick = useCallback(() => {
    if (!delivery?.id) {
      throw new Error('Delivery id not found');
    }

    navigateToDeliveryDetailPage(delivery.id, delivery.status);
  }, [delivery, navigateToDeliveryDetailPage]);

  return (
    <DeliveryChip variant={variant} disabled={!delivery || isLoadingDelivery} onClick={onClick}>
      {lang.TurnaroundDeliveryChip.turnaround}
    </DeliveryChip>
  );
};
